import { create } from 'zustand';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { isAdminEmail } from '@/lib/admin';

type AuthStatus = 'loading' | 'signed-in' | 'signed-out';

interface AuthState {
  user: User | null;
  status: AuthStatus;
  error: string | null;

  initAuthListener: () => () => void;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: 'loading',
  error: null,

  initAuthListener: () => {
    return onAuthStateChanged(auth(), async (user) => {
      // 화이트리스트 외 계정은 자동 로그아웃 (현재 운영자 1인 도구)
      if (user && !isAdminEmail(user.email)) {
        await fbSignOut(auth());
        set({
          user: null,
          status: 'signed-out',
          error: '현재 운영자 계정만 로그인할 수 있습니다. 청첩장 제작 의뢰는 크몽으로 부탁드립니다.',
        });
        return;
      }
      if (user) {
        await ensureUserDoc(user);
        set({ user, status: 'signed-in', error: null });
      } else {
        set({ user: null, status: 'signed-out' });
      }
    });
  },

  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth(), provider);
    } catch (e) {
      set({ error: extractAuthError(e) });
    }
  },

  signOut: async () => {
    await fbSignOut(auth());
    set({ user: null, status: 'signed-out' });
  },

  clearError: () => set({ error: null }),
}));

async function ensureUserDoc(user: User) {
  const ref = doc(db(), 'users', user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      email: user.email ?? '',
      displayName: user.displayName ?? '',
      photoURL: user.photoURL ?? '',
      phone: user.phoneNumber ?? '',
      createdAt: serverTimestamp(),
    });
  }
}

function extractAuthError(e: unknown): string {
  if (e instanceof Error) return e.message;
  return '알 수 없는 오류가 발생했습니다';
}
